/**
 * API Configuration Service — runtime server URL resolution.
 *
 * WHY THIS EXISTS:
 * The Android app (Capacitor WebView) ships the SPA as local assets served from
 * `https://localhost`. Build-time env vars (`VITE_API_URL`) are baked into the
 * bundle at compile time, which would force a rebuild every time the backend
 * moves (dev machine IP change, LAN server, production…). This service adds a
 * RUNTIME override persisted in IndexedDB so the APK can be pointed at any
 * backend without rebuilding (Settings → Serveur).
 *
 * BOOTSTRAP CONTRACT (IMPORTANT):
 * Modules that create API clients (lib/rpc-client.ts, lib/auth.ts,
 * services/api.ts) bake the base URL when they are first imported. Because ES
 * module graphs execute static imports before any app code runs, `main.tsx`
 * MUST await `initApiConfig()` BEFORE dynamically importing the app tree.
 * After that point, `getApiBaseUrl()` is safe to call synchronously anywhere.
 *
 * STORAGE:
 * Uses IndexedDB (never localStorage/sessionStorage — see custom ESLint rule
 * `no-localstorage`), mirroring the proven pattern of services/themeStorage.ts.
 */

/* eslint-disable no-undef */
// IndexedDB is a standard browser API — same pragma as themeStorage.ts.

/** Default base URL baked at build time by Vite (fail-fast enforced in vite.config.ts). */
const DEFAULT_API_BASE_URL = normalizeBaseUrl(import.meta.env.VITE_API_URL ?? '');

const DB_NAME = 'HarmonyAppConfigDB';
const DB_VERSION = 1;
const STORE_NAME = 'serverConfig';

/**
 * Shape of the persisted record (single-record design, keyPath 'id').
 * `apiUrl: null` means "no override — use the build-time default".
 */
interface ServerConfigRecord {
  id: 'current';
  apiUrl: string | null;
  lastUpdated: number;
}

/** In-memory effective base URL, set once by initApiConfig() and kept in sync on overrides. */
let effectiveBaseUrl = DEFAULT_API_BASE_URL;

/** Whether initApiConfig() has completed. Guards against bootstrap-order mistakes. */
let initialized = false;

/**
 * Normalize a base URL: trim whitespace and strip trailing slashes so callers
 * can safely concatenate `${base}/api/...` without double slashes.
 *
 * @param url - Raw base URL (may be empty string = same-origin relative calls)
 * @returns Normalized base URL ('' allowed — same-origin mode)
 */
function normalizeBaseUrl(url: string): string {
  return url.trim().replace(/\/+$/, '');
}

/**
 * Validate an override URL entered by the user.
 * Must be an absolute http(s) URL (relative overrides make no sense inside
 * the Capacitor WebView whose origin is the internal asset server).
 *
 * @param url - Candidate URL (already trimmed by the caller)
 * @returns true when usable as an API base URL
 */
export function isValidHttpUrl(url: string): boolean {
  if (!/^https?:\/\//i.test(url)) {
    return false;
  }
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Open (and upgrade) the IndexedDB database holding the server config.
 * Mirrors themeStorage's lazy-init pattern.
 */
async function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error('Failed to open IndexedDB for server config'));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

/**
 * Read the persisted override (null when unset or on any storage failure —
 * a broken IndexedDB must never brick the app: fall back to build defaults).
 */
async function readStoredOverride(): Promise<string | null> {
  try {
    const db = await openDB();
    const store = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME);
    const request = store.get('current');

    const record = await new Promise<ServerConfigRecord | undefined>((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(new Error('Failed to read server config'));
    });

    return typeof record?.apiUrl === 'string' && record.apiUrl.length > 0
      ? normalizeBaseUrl(record.apiUrl)
      : null;
  } catch (error) {
    console.warn('Failed to read server config override:', error);
    return null;
  }
}

/**
 * Persist the override (null deletes the record = reset to default).
 */
async function writeStoredOverride(apiUrl: string | null): Promise<void> {
  const db = await openDB();
  const store = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME);

  if (apiUrl === null) {
    store.delete('current');
    return;
  }

  const record: ServerConfigRecord = { id: 'current', apiUrl, lastUpdated: Date.now() };
  store.put(record);
}

/**
 * Bootstrap hook — MUST be awaited before importing modules that create API
 * clients (see module docblock). Loads the override and freezes the effective
 * base URL for the rest of the session.
 */
export async function initApiConfig(): Promise<void> {
  if (initialized) {
    return;
  }

  const override = await readStoredOverride();
  if (override !== null) {
    effectiveBaseUrl = override;
  }
  initialized = true;

  if (override !== null) {
    console.debug(`[apiConfig] Using runtime server override: ${override}`);
  }
}

/**
 * Effective API base URL (no trailing slash, '' = same-origin).
 * Call ONLY after initApiConfig() resolved (guaranteed by the main.tsx bootstrap).
 */
export function getApiBaseUrl(): string {
  return effectiveBaseUrl;
}

/**
 * Build-time default (VITE_API_URL), useful in the UI to show what the
 * runtime override is diverging from.
 */
export function getDefaultApiBaseUrl(): string {
  return DEFAULT_API_BASE_URL;
}

/**
 * Whether a runtime override is currently active (effective ≠ build default).
 */
export function hasServerUrlOverride(): boolean {
  return initialized && effectiveBaseUrl !== DEFAULT_API_BASE_URL;
}

/**
 * Persist a new override AND apply it in memory.
 * The app still requires a reload afterwards: rpc/auth/axios clients captured
 * the URL at import time (single-page live repointing is deliberately out of
 * scope — a reload is instant in the WebView and removes an entire class of
 * stale-client bugs).
 *
 * @param rawUrl - Absolute http(s) URL, or '' / null to reset to the default
 * @throws Error when the value is neither empty nor a valid absolute URL
 */
export async function setServerUrlOverride(rawUrl: string | null): Promise<void> {
  const normalized = rawUrl === null ? '' : normalizeBaseUrl(rawUrl);

  if (normalized.length > 0 && !isValidHttpUrl(normalized)) {
    throw new Error(`URL serveur invalide : « ${rawUrl} » (http:// ou https:// requis)`);
  }

  await writeStoredOverride(normalized.length > 0 ? normalized : null);

  effectiveBaseUrl = normalized.length > 0 ? normalized : DEFAULT_API_BASE_URL;
}
